import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Tabs, Tab, Alert, Card, CardContent,
  Checkbox, IconButton, Tooltip, Divider, Stack, CircularProgress
} from '@mui/material';
import {
  CreditCard as FinancialIcon, AttachMoney as PaymentIcon,
  Receipt as InvoiceIcon, Email as EmailIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, PictureAsPdf as PrintIcon, Delete as DeleteIcon,
  LocalOffer as DiscountIcon, Info as InfoIcon, Lock as LockIcon,
  Send as SendIcon, Edit as EditIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import Logo from '../../Commun/components/Logo';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { academyStore } from '../services/academyStore';
import { academyApi, toLocalDateStr } from '../services/academyApi';
import { Payment, PaymentMethod, Student, Invoice } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { useAuth } from '../../Commun/contexts/AuthContext';

export const FinancialPage: React.FC = () => {
  const { t, language } = useLanguage();
  const isRtl = language === 'ar';
  const { user } = useAuth();
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');
  const [, setTick] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openDailyReportModal, setOpenDailyReportModal] = useState(false);
  const [bulkReminderSuccess, setBulkReminderSuccess] = useState<string | null>(null);

  // Receipt Print State
  const [openReceiptModal, setOpenReceiptModal] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState<Payment | null>(null);

  // Invoice Print State
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [selectedPrintInvoice, setSelectedPrintInvoice] = useState<Invoice | null>(null);

  // Form State
  const [selectedStudentId, setSelectedStudentId] = useState<number>(1);
  const [paymentAmount, setPaymentAmount] = useState<number>(500);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Cash');
  const [paymentResultMessage, setPaymentResultMessage] = useState<string | null>(null);
  const [paymentAlertSeverity, setPaymentAlertSeverity] = useState<'success' | 'error'>('success');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Bulk Debtors Selection
  const [selectedDebtorStudentIds, setSelectedDebtorStudentIds] = useState<number[]>([]);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Discount Management Modal State
  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [selectedDiscountInvoice, setSelectedDiscountInvoice] = useState<Invoice | null>(null);
  const [selectedDiscountId, setSelectedDiscountId] = useState<number | 'custom' | 0>(0);
  const [customDiscountPercent, setCustomDiscountPercent] = useState<number>(0);
  const [discountJustification, setDiscountJustification] = useState<string>('');
  const [discountSubmitting, setDiscountSubmitting] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        academyStore._refresh('academy_invoice', () => academyApi.getInvoices(false)),
        academyStore._refresh('academy_payment', () => academyApi.getPayments(false)),
        academyStore._refresh('academy_discount_request', () => academyApi.getDiscountRequests(false)),
        academyStore._refresh('academy_student', () => academyApi.getStudents()),
        academyStore._refresh('academy_group', () => academyApi.getGroups()),
      ]);
      setTick(t => t + 1);
      blobticMessage.success(t('common.messages.success'), t('common.refreshed') || 'Données actualisées avec succès.');
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || 'Erreur lors de l\'actualisation.');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    return academyStore.subscribe(() => setTick(t => t + 1));
  }, []);

  // Connected Agency Details directly from User context (populated at login)
  const rawAgency = user?.agency;
  const parsedDosId = rawAgency ? parseInt(String(rawAgency), 10) : NaN;
  const connectedDosId = (!isNaN(parsedDosId) && parsedDosId > 0) ? parsedDosId : 1;

  const isUserAdmin = Boolean(user?.isAdmin || user?.profileId === 1 || (user?.profileLib && user.profileLib.toLowerCase().includes('admin')));

  const agencyLib = user?.dosLib || user?.agency || getParam('DOS_LIB') || getParam('Agency_Name') || 'BLOBTIC ACADEMY';
  const agencyTel = user?.dosTelph || getParam('DOS_TELPH') || getParam('Agency_Phone') || '';
  const agencyEmail = user?.dosEmail || getParam('DOS_EMAIL') || getParam('Agency_Email') || '';
  const agencyAdr = user?.dosAdr1 || getParam('DOS_ADR1') || getParam('Agency_Address') || '';
  const agencyLogo = user?.dosLogo;

  // Clôture de l'agence (DOS_CLOSEDPLNG)
  const closedDateStr = user?.dosClosedPlng ? user.dosClosedPlng.split('T')[0] : '';
  const isInvoiceClosed = (inv: Invoice): boolean => {
    if (!closedDateStr) return false;
    const invDate = (inv.issue_date || '').split('T')[0];
    return !!invDate && invDate <= closedDateStr;
  };

  const invoices = academyStore.getInvoices();
  const payments = academyStore.getPayments();
  const students = academyStore.getStudents();
  const groups = academyStore.getGroups();
  const discounts = academyStore.getDiscounts();
  const discountRequests = academyStore.getDiscountRequests();

  const totalOutstanding = academyStore.getTotalOutstanding();
  const monthlyRevenue = academyStore.getMonthlyRevenue();

  // Daily Cash Report Logic
  const todayStr = toLocalDateStr(new Date());
  const todayPayments = payments.filter(p => (p.payment_date || '').startsWith(todayStr));
  const todayCash = todayPayments.filter(p => p.method === 'Cash').reduce((acc, p) => acc + p.amount, 0);
  const todayCard = todayPayments.filter(p => p.method === 'Card').reduce((acc, p) => acc + p.amount, 0);
  const todayTransfer = todayPayments.filter(p => p.method === 'Transfer').reduce((acc, p) => acc + p.amount, 0);
  const todayTotal = todayCash + todayCard + todayTransfer;

  const handleOpenReceivePayment = (studentId?: number) => {
    if (studentId) setSelectedStudentId(studentId);
    setPaymentAmount(500);
    setPaymentMethod('Cash');
    setPaymentResultMessage(null);
    setPaymentAlertSeverity('success');
    setOpenPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    setPaymentResultMessage(null);
    setPaymentLoading(true);

    try {
      const res = await academyStore.recordPayment(selectedStudentId, paymentAmount, paymentMethod);
      if (res.success) {
        setPaymentAlertSeverity('success');
        setPaymentResultMessage(
          res.overpayment != null && res.overpayment > 0
            ? t('academy.financial.success.overpayment', res.overpayment)
            : t('academy.financial.success.allocated')
        );
        setTimeout(() => setOpenPaymentModal(false), 1500);
      } else {
        setPaymentAlertSeverity('error');
        setPaymentResultMessage(
          res.message === 'Student has no outstanding unpaid invoices.'
            || String(res.message || '').includes('ERR_NO_UNPAID')
            ? t('academy.financial.error.no_invoices')
            : t('academy.financial.error.payment')
        );
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleOpenReceipt = (pmt: Payment) => {
    setSelectedReceiptPayment(pmt);
    setOpenReceiptModal(true);
  };

  const handleOpenInvoicePrint = (inv: Invoice) => {
    setSelectedPrintInvoice(inv);
    setOpenInvoiceModal(true);
  };

  const handleOpenDiscountModal = (inv: Invoice) => {
    setSelectedDiscountInvoice(inv);
    if (inv.discount_id) {
      setSelectedDiscountId(inv.discount_id);
      setCustomDiscountPercent(inv.discount_percent || 0);
    } else if (inv.discount_percent && inv.discount_percent > 0) {
      setSelectedDiscountId('custom');
      setCustomDiscountPercent(inv.discount_percent);
    } else {
      setSelectedDiscountId(0);
      setCustomDiscountPercent(0);
    }
    setDiscountJustification(inv.discount_reason || '');
    setOpenDiscountModal(true);
  };

  const selectedDiscountObj = typeof selectedDiscountId === 'number' && selectedDiscountId > 0
    ? discounts.find(d => d.id === selectedDiscountId)
    : null;

  const effectiveDiscountPercent = selectedDiscountId === 0
    ? 0
    : selectedDiscountId === 'custom'
      ? customDiscountPercent
      : (selectedDiscountObj?.percentage || 0);

  const invoiceGrossAmount = selectedDiscountInvoice
    ? (selectedDiscountInvoice.original_amount || selectedDiscountInvoice.total_amount)
    : 0;

  const computedDiscountAmount = Math.round((invoiceGrossAmount * effectiveDiscountPercent) / 100);
  const computedNewNetAmount = Math.max(0, invoiceGrossAmount - computedDiscountAmount);
  const invoicePaidAmount = selectedDiscountInvoice?.amount_paid || 0;
  const computedNewDueRest = Math.max(0, computedNewNetAmount - invoicePaidAmount);

  // Direct application condition:
  // Case 1: user is Admin OR discount validator_role is 'Planificateur' or 'Auto' OR discount is 0 (removal)
  // Case 2: user is NOT Admin and validator_role is 'Admin' => Pending request
  const isDirectApply = isUserAdmin || !selectedDiscountObj || selectedDiscountObj.validator_role === 'Planificateur' || selectedDiscountObj.validator_role === 'Auto' || effectiveDiscountPercent === 0;

  const handleSaveDiscount = async () => {
    if (!selectedDiscountInvoice) return;
    if (!discountJustification.trim()) {
      blobticMessage.error(t('common.error'), t('academy.financial.discount.justification_required'));
      return;
    }

    setDiscountSubmitting(true);
    try {
      if (isDirectApply) {
        // Case 1: Direct application
        const res = await academyStore.applyInvoiceDiscountDirect(selectedDiscountInvoice.id, {
          discount_id: selectedDiscountObj?.id,
          discount_code: selectedDiscountObj?.code || (effectiveDiscountPercent > 0 ? 'MANUAL' : undefined),
          discount_percent: effectiveDiscountPercent,
          discount_amount: computedDiscountAmount,
          original_amount: invoiceGrossAmount,
          new_amount: computedNewNetAmount,
          justification: discountJustification.trim(),
          user_lib: user?.name || user?.code
        });
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('academy.financial.discount.success_applied'));
          setOpenDiscountModal(false);
        } else {
          blobticMessage.error(t('common.error'), res.message || t('academy.discount_validations.approve_error'));
        }
      } else {
        // Case 2: Pending request to admin
        const res = await academyStore.submitDiscountRequest({
          invoice_id: selectedDiscountInvoice.id,
          student_id: selectedDiscountInvoice.student_id,
          discount_id: selectedDiscountObj?.id,
          discount_code: selectedDiscountObj?.code,
          discount_percent: effectiveDiscountPercent,
          discount_amount: computedDiscountAmount,
          original_amount: invoiceGrossAmount,
          new_amount: computedNewNetAmount,
          justification: discountJustification.trim(),
          dos_idf: connectedDosId
        });
        if (res.success) {
          blobticMessage.info(t('academy.financial.discount.success_submitted'), t('academy.financial.discount.pending_admin_notice'));
          setOpenDiscountModal(false);
        } else {
          blobticMessage.error(t('common.error'), res.message || t('academy.discount_validations.reject_error'));
        }
      }
    } finally {
      setDiscountSubmitting(false);
    }
  };

  // boubaker 23/08/2026 - confirmations/alertes via le composant générique animé (blobticMessage)
  const handleDeletePayment = async (pmt: Payment) => {
    const std = students.find(s => s.id === pmt.student_id);
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t(
        'academy.financial.delete_payment_confirm',
        pmt.receipt_number,
        pmt.amount,
        currencySymbol,
        `${std?.first_name || ''} ${std?.last_name || ''}`
      ),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    });
    if (!confirmed) return;

    const res = await academyStore.deletePayment(pmt.id);
    if (res.success) {
      blobticMessage.success(t('common.messages.success'), t('academy.financial.msg_payment_deleted', pmt.receipt_number));
    } else {
      blobticMessage.error(t('common.error'), res.message || t('academy.financial.error.delete_payment'));
    }
  };

  // boubaker 23/08/2026 - suppression facture : contrôles serveur (hook Before Delete) :
  // aucun reçu de paiement rattaché + étudiant détaché du groupe lié à la facture
  const handleDeleteInvoice = async (inv: Invoice) => {
    if (isInvoiceClosed(inv)) {
      blobticMessage.error(t('common.error'), t('academy.financial.error_closed_invoices', closedDateStr));
      return;
    }
    const std = students.find(s => s.id === inv.student_id);
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t(
        'academy.financial.delete_invoice_confirm',
        inv.invoice_number || '',
        `${std?.first_name || ''} ${std?.last_name || ''}`
      ),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    });
    if (!confirmed) return;

    const res = await academyStore.deleteInvoice(inv.id);
    if (res.success) {
      blobticMessage.success(t('common.messages.success'), t('academy.financial.msg_invoice_deleted', inv.invoice_number || ''));
    } else {
      blobticMessage.error(t('common.error'), res.message || t('academy.financial.error.delete_invoice'));
    }
  };

  const handleBulkReminder = async () => {
    if (selectedDebtorStudentIds.length === 0) {
      blobticMessage.warning(t('common.messages.warning'), t('academy.financial.bulk_reminder_no_selection'));
      return;
    }

    setSendingReminders(true);
    try {
      const res = await academyApi.sendBulkDebtReminder(selectedDebtorStudentIds, language);
      if (res.success && res.data) {
        const { sentCount, skippedCount, failedCount } = res.data;
        const msg = t('academy.financial.bulk_reminder_result', sentCount, skippedCount);
        if (sentCount > 0) {
          blobticMessage.success(t('common.messages.success'), msg);
        } else if (skippedCount > 0 || failedCount > 0) {
          blobticMessage.warning(t('common.messages.warning'), msg);
        } else {
          blobticMessage.info(t('common.messages.info'), msg);
        }
        setSelectedDebtorStudentIds([]);
      } else {
        blobticMessage.error(t('common.error'), res.message || t('common.error'));
      }
    } catch (err: any) {
      blobticMessage.error(t('common.error'), err?.message || t('common.error'));
    } finally {
      setSendingReminders(false);
    }
  };

  // Receipt breakdown calculation
  const receiptInvoice = selectedReceiptPayment ? invoices.find(i => i.id === selectedReceiptPayment.invoice_id) : null;
  const receiptStudent = selectedReceiptPayment ? students.find(s => s.id === selectedReceiptPayment.student_id) : null;
  const receiptGroup = receiptInvoice ? groups.find(g => g.id === receiptInvoice.group_id) : null;

  const totalInvoicedForStudent = receiptStudent ? invoices.filter(i => i.student_id === receiptStudent.id).reduce((a, b) => a + b.total_amount, 0) : 0;
  const totalPaidForStudent = receiptStudent ? invoices.filter(i => i.student_id === receiptStudent.id).reduce((a, b) => a + b.amount_paid, 0) : 0;
  const restToPayForStudent = Math.max(0, totalInvoicedForStudent - totalPaidForStudent);

  // Invoice Print breakdown calculation
  const printStudent = selectedPrintInvoice ? students.find(s => s.id === selectedPrintInvoice.student_id) : null;
  const printGroup = selectedPrintInvoice ? groups.find(g => g.id === selectedPrintInvoice.group_id) : null;
  const printDueRest = selectedPrintInvoice ? Math.max(0, selectedPrintInvoice.total_amount - selectedPrintInvoice.amount_paid) : 0;

  const currentUserName = user?.name || user?.code;
  const showUserOnEditions = academyStore.getShowUserOnEditions(connectedDosId);

  const getInvoiceStatusLabel = (status: string) => {
    if (status === 'Paid') return t('academy.financial.status.paid');
    if (status === 'Partial') return t('academy.financial.status.partial');
    return t('academy.financial.status.unpaid');
  };

  const getPaymentMethodLabel = (method: string) => {
    if (method === 'Cash') return t('academy.financial.method.cash');
    if (method === 'Card') return t('academy.financial.method.card');
    return t('academy.financial.method.transfer');
  };

  return (
    <GenericPage
      title={t('academy.financial.title')}
      icon={<FinancialIcon />}
      footerEnabled={false}
      additionalActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            startIcon={isRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{ borderRadius: 2 }}
          >
            {t('common.refresh') || 'Actualiser'}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={() => setOpenDailyReportModal(true)}
            sx={{ borderRadius: 2 }}
          >
            {t('academy.financial.daily_report')}
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<PaymentIcon />}
            onClick={() => handleOpenReceivePayment()}
            sx={{ borderRadius: 2 }}
          >
            {t('academy.financial.receive_payment')}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ pb: 4 }}>
        {/* CSS rules to hide action buttons and modals controls during printing */}
        <style>
          {`
            @media print {
              body * {
                visibility: hidden;
              }
              .MuiDialog-root, .MuiDialog-root * {
                visibility: visible;
              }
              .MuiDialogActions-root,
              .MuiDialogTitle-root,
              button,
              .no-print,
              .MuiIconButton-root {
                display: none !important;
                visibility: hidden !important;
              }
              .MuiDialog-paper {
                box-shadow: none !important;
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                border: none !important;
              }
              .MuiDialogContent-root {
                padding: 16px !important;
                overflow: visible !important;
              }
              #printable-invoice, #printable-receipt, #printable-report {
                visibility: visible !important;
                width: 100% !important;
                padding: 10px !important;
              }
            }
          `}
        </style>

        {/* Reminder Banner */}
        {bulkReminderSuccess && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            {bulkReminderSuccess}
          </Alert>
        )}

        {/* Manual Discount Info Banner */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          {t('academy.financial.manual_discount_info')}
        </Alert>

        {/* Summary KPI Bar */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #ef4444' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {t('academy.financial.kpi.debt')}
                </Typography>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {currencySymbol}{totalOutstanding.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #10b981' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {t('academy.financial.kpi.monthly')}
                </Typography>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {currencySymbol}{monthlyRevenue.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #6366f1' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {t('academy.financial.kpi.today')}
                </Typography>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {currencySymbol}{todayTotal.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs View */}
        <Paper elevation={2} sx={{ borderRadius: 3, p: 2 }}>
          <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label={t('academy.financial.tab.debts')} />
            <Tab label={t('academy.financial.tab.invoices')} />
            <Tab label={t('academy.financial.tab.payments')} />
          </Tabs>

          {/* Tab 0: Student Debts & Bulk Reminder */}
          {tabIndex === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">{t('academy.financial.debtors_list')}</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={sendingReminders ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
                  disabled={selectedDebtorStudentIds.length === 0 || sendingReminders}
                  onClick={handleBulkReminder}
                  sx={{ borderRadius: 2 }}
                >
                  {sendingReminders ? t('academy.financial.bulk_reminder_sending') : t('academy.financial.send_bulk_reminder', selectedDebtorStudentIds.length)}
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          onChange={(e) => {
                            if (e.target.checked) {
                              const debtorIds = students.filter(s => {
                                const sInvs = invoices.filter(i => i.student_id === s.id);
                                return (sInvs.reduce((a, b) => a + b.total_amount, 0) - sInvs.reduce((a, b) => a + b.amount_paid, 0)) > 0;
                              }).map(s => s.id);
                              setSelectedDebtorStudentIds(debtorIds);
                            } else {
                              setSelectedDebtorStudentIds([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{t('academy.financial.table.student')}</TableCell>
                      <TableCell>{t('academy.financial.table.invoiced')}</TableCell>
                      <TableCell>{t('academy.financial.table.paid')}</TableCell>
                      <TableCell>{t('academy.financial.table.badge')}</TableCell>
                      <TableCell align="right">{t('academy.financial.table.action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map(student => {
                      const studentInvs = invoices.filter(i => i.student_id === student.id);
                      const totalInv = studentInvs.reduce((a, b) => a + b.total_amount, 0);
                      const totalPaid = studentInvs.reduce((a, b) => a + b.amount_paid, 0);
                      const restToPay = totalInv - totalPaid;

                      if (restToPay <= 0) return null;

                      const isChecked = selectedDebtorStudentIds.includes(student.id);

                      return (
                        <TableRow key={student.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDebtorStudentIds(prev => [...prev, student.id]);
                                } else {
                                  setSelectedDebtorStudentIds(prev => prev.filter(id => id !== student.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">{student.first_name} {student.last_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                          </TableCell>
                          <TableCell>{currencySymbol}{totalInv}</TableCell>
                          <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{currencySymbol}{totalPaid}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${t('academy.financial.rest_to_pay')}: ${currencySymbol}${restToPay}`}
                              color="error"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="contained" color="success" onClick={() => handleOpenReceivePayment(student.id)} sx={{ borderRadius: 2 }}>
                              {t('academy.financial.receive_payment')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 1: Invoices Ledger with Printable Invoice Feature */}
          {tabIndex === 1 && (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>{t('academy.financial.table.invoice_num')}</TableCell>
                    <TableCell>{t('academy.financial.table.student')}</TableCell>
                    <TableCell>{t('academy.financial.gross_amount')}</TableCell>
                    <TableCell>{t('academy.financial.applied_discount')}</TableCell>
                    <TableCell>{t('academy.financial.net_invoiced')}</TableCell>
                    <TableCell>{t('academy.financial.amount_paid')}</TableCell>
                    <TableCell>{t('academy.financial.table.due_date')}</TableCell>
                    <TableCell>{t('academy.financial.table.status')}</TableCell>
                    <TableCell align="right">{t('academy.financial.table.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(inv => {
                    const std = students.find(s => s.id === inv.student_id);
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{inv.invoice_number}</TableCell>
                        <TableCell>{std?.first_name} {std?.last_name}</TableCell>
                        <TableCell>{currencySymbol}{inv.original_amount || inv.total_amount}</TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {inv.discount_code ? (
                              <Chip
                                icon={<DiscountIcon />}
                                label={`${inv.discount_code} (-${inv.discount_percent}%)`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">{t('academy.financial.no_discount')}</Typography>
                            )}

                            {(() => {
                              const pendingReq = discountRequests.find(r => r.invoice_id === inv.id && r.status === 'Pending');
                              if (pendingReq) {
                                return (
                                  <Tooltip title={t('academy.financial.discount.pending_tooltip', pendingReq.discount_percent, pendingReq.justification)}>
                                    <Chip
                                      label={t('academy.financial.discount.pending_badge', pendingReq.discount_percent)}
                                      size="small"
                                      color="warning"
                                      sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                );
                              }
                              return null;
                            })()}

                            <Tooltip title={t('academy.financial.discount.manage_tooltip')} arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDiscountModal(inv)}
                                sx={{ ml: 0.5 }}
                              >
                                <DiscountIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ fontWeight: 'bold' }}>{currencySymbol}{inv.total_amount}</TableCell>
                        <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{currencySymbol}{inv.amount_paid}</TableCell>
                        <TableCell>{inv.due_date}</TableCell>
                        <TableCell>
                          <Chip label={getInvoiceStatusLabel(inv.status)} size="small" color={inv.status === 'Paid' ? 'success' : inv.status === 'Partial' ? 'warning' : 'error'} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<PrintIcon />}
                              onClick={() => handleOpenInvoicePrint(inv)}
                              sx={{ borderRadius: 2 }}
                            >
                              {t('academy.financial.print_invoice')}
                            </Button>
                            {/* boubaker 23/08/2026 - suppression contrôlée côté serveur (paiements / rattachement groupe / clôture agence) */}
                            {isInvoiceClosed(inv) ? (
                              <Tooltip title={t('academy.financial.invoice_locked_tooltip') || "Facture verrouillée (période d'agence clôturée)"} arrow>
                                <span>
                                  <IconButton size="small" disabled sx={{ opacity: 0.4 }}>
                                    <LockIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title={t('academy.financial.delete_invoice_tooltip')} arrow>
                                <IconButton size="small" color="error" onClick={() => handleDeleteInvoice(inv)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 2: Payments History with Receipt Print & Delete Actions */}
          {tabIndex === 2 && (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>{t('academy.financial.table.receipt_num')}</TableCell>
                    <TableCell>{t('academy.financial.table.student')}</TableCell>
                    <TableCell>{t('academy.financial.table.amount')}</TableCell>
                    <TableCell>{t('academy.financial.table.method')}</TableCell>
                    <TableCell>{t('academy.financial.table.date')}</TableCell>
                    <TableCell align="right">{t('academy.financial.table.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map(pmt => {
                    const std = students.find(s => s.id === pmt.student_id);
                    return (
                      <TableRow key={pmt.id} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{pmt.receipt_number}</TableCell>
                        <TableCell>{std?.first_name} {std?.last_name}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>{currencySymbol}{pmt.amount}</TableCell>
                        <TableCell><Chip label={getPaymentMethodLabel(pmt.method)} size="small" variant="outlined" /></TableCell>
                        <TableCell>{pmt.payment_date}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title={t('academy.financial.print_receipt')} arrow>
                              <Button size="small" variant="outlined" color="primary" startIcon={<PrintIcon />} onClick={() => handleOpenReceipt(pmt)} sx={{ borderRadius: 2 }}>
                                {t('academy.financial.print_receipt')}
                              </Button>
                            </Tooltip>
                            <Tooltip title={t('academy.financial.delete_payment_tooltip')} arrow>
                              <IconButton size="small" color="error" onClick={() => handleDeletePayment(pmt)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Official Printable Invoice Modal */}
        <Dialog open={openInvoiceModal} onClose={() => setOpenInvoiceModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }} className="no-print">
            <Typography variant="h6" fontWeight="bold">{agencyLib}</Typography>
            <Typography variant="caption" color="text.secondary">{t('academy.financial.invoice_details')}</Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedPrintInvoice && printStudent && (
              <Box id="printable-invoice" dir={isRtl ? 'rtl' : 'ltr'} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Agency Header with Dynamic Connected Agency Details & Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid', borderColor: 'primary.main', pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Logo variant="primary" height={45} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1.2 }}>
                        {agencyLib}
                      </Typography>
                      {agencyAdr && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {agencyAdr}
                        </Typography>
                      )}
                      {(agencyTel || agencyEmail) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {[agencyTel && `${t('academy.financial.phone_label')} ${agencyTel}`, agencyEmail && `${t('academy.financial.email_label')} ${agencyEmail}`].filter(Boolean).join(' | ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: isRtl ? 'left' : 'right', flexShrink: 0, whiteSpace: 'nowrap', pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}>
                    <Chip label={t('academy.financial.official')} color="primary" size="small" sx={{ fontWeight: 'bold', mb: 0.5 }} />
                    <Typography variant="subtitle2" fontWeight="bold" display="block" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.invoice_no')} : {selectedPrintInvoice.invoice_number}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.due_date_label')} : {selectedPrintInvoice.due_date}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.status_label')} : {getInvoiceStatusLabel(selectedPrintInvoice.status)}
                    </Typography>
                  </Box>
                </Box>

                {/* Document Type Title Banner */}
                <Box sx={{ textAlign: 'center', py: 0.8, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                    {t('academy.financial.invoice_type_title')}
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('academy.financial.student_group_info')}</Typography>
                  <Typography variant="body2"><strong>{t('academy.financial.full_name')}</strong> {printStudent.first_name} {printStudent.last_name}</Typography>
                  <Typography variant="body2"><strong>{t('academy.financial.email_label')}</strong> {printStudent.email || 'N/A'} | <strong>{t('academy.financial.phone_label')}</strong> {printStudent.phone || 'N/A'}</Typography>
                  {printGroup && <Typography variant="body2"><strong>{t('academy.financial.course_group_label')}</strong> {printGroup.name}</Typography>}
                </Box>

                <Table size="small">
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      <TableCell>{t('academy.financial.designation')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'}>{t('academy.financial.amount')} ({currencySymbol})</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('academy.financial.gross_amount')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'}>{selectedPrintInvoice.original_amount || selectedPrintInvoice.total_amount}</TableCell>
                    </TableRow>

                    {selectedPrintInvoice.discount_code && (
                      <TableRow>
                        <TableCell>
                          {t('academy.financial.applied_discount')} ({selectedPrintInvoice.discount_code} -{selectedPrintInvoice.discount_percent}%)
                        </TableCell>
                        <TableCell align={isRtl ? 'left' : 'right'} sx={{ color: 'error.main' }}>
                          -{selectedPrintInvoice.discount_amount}
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('academy.financial.net_invoiced')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'} sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>
                        {selectedPrintInvoice.total_amount} {currencySymbol}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ color: 'success.main' }}>{t('academy.financial.amount_paid')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'} sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        {selectedPrintInvoice.amount_paid} {currencySymbol}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color={printDueRest > 0 ? 'error.main' : 'success.main'}>
                      {t('academy.financial.rest_to_pay')} : {printDueRest} {currencySymbol}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: 160, border: '1px dashed text.disabled', p: 1, borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight="bold" display="block">{t('academy.financial.stamp_signature')}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 3 }}>{t('academy.financial.direction')} {agencyLib}</Typography>
                  </Box>
                </Box>

                {/* Edition Footer */}
                {showUserOnEditions && (
                  <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>{t('academy.financial.issued_by')}</strong> {currentUserName} {user?.profileLib ? `(${user.profileLib})` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agencyLib} | {new Date().toLocaleDateString(language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions className="no-print">
            <Button onClick={() => setOpenInvoiceModal(false)}>{t('btn.close')}</Button>
            <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={() => window.print()}>
              {t('academy.financial.print_invoice')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Official Payment Receipt Modal */}
        <Dialog open={openReceiptModal} onClose={() => setOpenReceiptModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }} className="no-print">
            <Typography variant="h6" fontWeight="bold">{agencyLib}</Typography>
            <Typography variant="caption" color="text.secondary">{t('academy.financial.receipt_details')}</Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedReceiptPayment && receiptStudent && (
              <Box id="printable-receipt" dir={isRtl ? 'rtl' : 'ltr'} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Agency Header with Dynamic Connected Agency Details & Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid', borderColor: 'primary.main', pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Logo variant="primary" height={45} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1.2 }}>
                        {agencyLib}
                      </Typography>
                      {agencyAdr && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {agencyAdr}
                        </Typography>
                      )}
                      {(agencyTel || agencyEmail) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {[agencyTel && `${t('academy.financial.phone_label')} ${agencyTel}`, agencyEmail && `${t('academy.financial.email_label')} ${agencyEmail}`].filter(Boolean).join(' | ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: isRtl ? 'left' : 'right', flexShrink: 0, whiteSpace: 'nowrap', pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}>
                    <Chip label={t('academy.financial.official')} color="success" size="small" sx={{ fontWeight: 'bold', mb: 0.5 }} />
                    <Typography variant="subtitle2" fontWeight="bold" display="block" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.receipt_no')} : {selectedReceiptPayment.receipt_number}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.date_label')} : {selectedReceiptPayment.payment_date}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {t('academy.financial.mode_label')} : {getPaymentMethodLabel(selectedReceiptPayment.method)}
                    </Typography>
                  </Box>
                </Box>

                {/* Document Type Title Banner */}
                <Box sx={{ textAlign: 'center', py: 0.8, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                    {t('academy.financial.receipt_type_title')}
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t('academy.financial.student_info')}</Typography>
                  <Typography variant="body2"><strong>{t('academy.financial.full_name')}</strong> {receiptStudent.first_name} {receiptStudent.last_name}</Typography>
                  <Typography variant="body2"><strong>{t('academy.financial.email_label')}</strong> {receiptStudent.email || 'N/A'} | <strong>{t('academy.financial.phone_label')}</strong> {receiptStudent.phone || 'N/A'}</Typography>
                  {receiptGroup && <Typography variant="body2"><strong>{t('academy.financial.course_group_label')}</strong> {receiptGroup.name}</Typography>}
                </Box>

                <Table size="small">
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      <TableCell>{t('academy.financial.description')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'}>{t('academy.financial.amount')} ({currencySymbol})</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receiptInvoice && (
                      <>
                        <TableRow>
                          <TableCell>{t('academy.financial.gross_amount_course')}</TableCell>
                          <TableCell align={isRtl ? 'left' : 'right'}>{receiptInvoice.original_amount || receiptInvoice.total_amount}</TableCell>
                        </TableRow>
                        {receiptInvoice.discount_code && (
                          <TableRow>
                            <TableCell>{t('academy.financial.applied_discount')} ({receiptInvoice.discount_code} -{receiptInvoice.discount_percent}%)</TableCell>
                            <TableCell align={isRtl ? 'left' : 'right'} sx={{ color: 'error.main' }}>-{receiptInvoice.discount_amount}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('academy.financial.net_invoiced')}</TableCell>
                          <TableCell align={isRtl ? 'left' : 'right'} sx={{ fontWeight: 'bold' }}>{receiptInvoice.total_amount}</TableCell>
                        </TableRow>
                      </>
                    )}
                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>{t('academy.financial.payment_amount')}</TableCell>
                      <TableCell align={isRtl ? 'left' : 'right'} sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1.1rem' }}>
                        {selectedReceiptPayment.amount} {currencySymbol}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" display="block">{t('academy.financial.total_paid_today')} : {totalPaidForStudent} {currencySymbol}</Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color={restToPayForStudent > 0 ? 'error.main' : 'success.main'}>
                      {t('academy.financial.rest_to_pay')} : {restToPayForStudent} {currencySymbol}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', minWidth: 160, border: '1px dashed text.disabled', p: 1, borderRadius: 2 }}>
                    <Typography variant="caption" fontWeight="bold" display="block">{t('academy.financial.stamp_signature')}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 3 }}>{t('academy.financial.direction')} {agencyLib}</Typography>
                  </Box>
                </Box>

                {/* Edition Footer */}
                {showUserOnEditions && (
                  <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>{t('academy.financial.issued_by')}</strong> {currentUserName} {user?.profileLib ? `(${user.profileLib})` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agencyLib} | {new Date().toLocaleDateString(language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR')}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions className="no-print">
            <Button onClick={() => setOpenReceiptModal(false)}>{t('btn.close')}</Button>
            <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={() => window.print()}>
              {t('academy.financial.print_receipt')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Receive Payment Modal */}
        <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('academy.financial.modal.receive_title')}</DialogTitle>
          <DialogContent dividers>
            {paymentResultMessage && (
              <Alert severity={paymentAlertSeverity} sx={{ mb: 2, borderRadius: 2 }}>
                {paymentResultMessage}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField select label={t('academy.financial.modal.select_student')} value={selectedStudentId} onChange={(e) => setSelectedStudentId(Number(e.target.value))} fullWidth required>
                {students.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.email})</MenuItem>
                ))}
              </TextField>

              <TextField label={t('academy.financial.modal.amount', currencySymbol)} type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(Number(e.target.value))} fullWidth required />

              <TextField select label={t('academy.financial.modal.method')} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)} fullWidth required>
                <MenuItem value="Cash">{t('academy.financial.modal.cash')}</MenuItem>
                <MenuItem value="Card">{t('academy.financial.modal.card')}</MenuItem>
                <MenuItem value="Transfer">{t('academy.financial.modal.transfer')}</MenuItem>
              </TextField>

              <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                {t('academy.financial.modal.fifo_note')}
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPaymentModal(false)} disabled={paymentLoading}>{t('btn.cancel')}</Button>
            <Button onClick={handleConfirmPayment} variant="contained" color="success" disabled={paymentLoading}>
              {t('academy.financial.modal.process')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Discount Management / Request Modal */}
        <Dialog open={openDiscountModal} onClose={() => setOpenDiscountModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiscountIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {t('academy.financial.discount.dialog_title')}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedDiscountInvoice && (() => {
              const invoiceStudent = students.find(s => s.id === selectedDiscountInvoice.student_id);
              return (
                <Stack spacing={2.5}>
                  {/* Invoice Summary Card */}
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('academy.print.col_invoice')}</Typography>
                        <Typography variant="subtitle2" fontWeight="bold">{selectedDiscountInvoice.invoice_number}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('academy.financial.discount.student_label')}</Typography>
                        <Typography variant="subtitle2" fontWeight="bold">{invoiceStudent?.first_name} {invoiceStudent?.last_name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('academy.financial.discount.initial_gross')}</Typography>
                        <Typography variant="body2" fontWeight="bold">{currencySymbol}{invoiceGrossAmount}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">{t('academy.financial.discount.already_paid')}</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">{currencySymbol}{invoicePaidAmount}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Discount Policy Selector */}
                  <TextField
                    select
                    label={t('academy.financial.discount.policy_label')}
                    value={selectedDiscountId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'custom') {
                        setSelectedDiscountId('custom');
                      } else {
                        setSelectedDiscountId(Number(val));
                      }
                    }}
                    fullWidth
                    helperText={t('academy.financial.discount.policy_helper')}
                  >
                    <MenuItem value={0}>
                      <em>{t('academy.financial.discount.none_option')}</em>
                    </MenuItem>
                    {discounts.filter(d => d.is_active).map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <span>{d.name} ({d.code})</span>
                          <Chip
                            label={`-${d.percentage}% (${d.validator_role === 'Planificateur' || d.validator_role === 'Auto' ? t('academy.financial.discount.validation_auto') : t('academy.financial.discount.validation_admin')})`}
                            size="small"
                            color={d.validator_role === 'Planificateur' || d.validator_role === 'Auto' ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        </Box>
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">{t('academy.financial.discount.custom_option')}</MenuItem>
                  </TextField>

                  {selectedDiscountId === 'custom' && (
                    <TextField
                      type="number"
                      label={t('academy.financial.discount.custom_percent_label')}
                      value={customDiscountPercent}
                      onChange={(e) => setCustomDiscountPercent(Math.min(100, Math.max(0, Number(e.target.value))))}
                      inputProps={{ min: 0, max: 100 }}
                      fullWidth
                    />
                  )}

                  {/* Dynamic Simulation Breakdown */}
                  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'primary.light', bgcolor: 'background.paper' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                        {t('academy.financial.discount.simulation_title')}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">{t('academy.financial.discount.gross_label')}</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">{currencySymbol}{invoiceGrossAmount}</Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="body2" color="secondary.main" fontWeight="bold">
                            {t('academy.financial.discount.deduction_label', effectiveDiscountPercent)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="secondary.main" fontWeight="bold">
                            -{currencySymbol}{computedDiscountAmount}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        <Grid item xs={6}>
                          <Typography variant="subtitle2" fontWeight="bold">{t('academy.financial.discount.new_net_invoiced')}</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                            {currencySymbol}{computedNewNetAmount}
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">{t('academy.financial.discount.new_due_rest')}</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" fontWeight="bold" color={computedNewDueRest > 0 ? 'error.main' : 'success.main'}>
                            {currencySymbol}{computedNewDueRest}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Mandatory Justification / Motif */}
                  <TextField
                    required
                    multiline
                    rows={3}
                    label={t('academy.financial.discount.justification_label')}
                    placeholder={t('academy.financial.discount.justification_placeholder')}
                    value={discountJustification}
                    onChange={(e) => setDiscountJustification(e.target.value)}
                    fullWidth
                    error={!discountJustification.trim()}
                    helperText={!discountJustification.trim() ? t('academy.financial.discount.justification_required') : ''}
                  />

                  {/* Role validation notice */}
                  {isDirectApply ? (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2 }}>
                      {isUserAdmin
                        ? t('academy.financial.discount.direct_admin_notice')
                        : t('academy.financial.discount.direct_planner_notice')}
                    </Alert>
                  ) : (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ borderRadius: 2 }}>
                      {t('academy.financial.discount.pending_admin_notice')}
                    </Alert>
                  )}
                </Stack>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDiscountModal(false)} disabled={discountSubmitting}>
              {t('btn.cancel')}
            </Button>
            <Button
              variant="contained"
              color={isDirectApply ? 'success' : 'warning'}
              startIcon={isDirectApply ? <CheckCircleIcon /> : <SendIcon />}
              onClick={handleSaveDiscount}
              disabled={discountSubmitting || !discountJustification.trim()}
            >
              {isDirectApply ? t('academy.financial.discount.btn_apply_direct') : t('academy.financial.discount.btn_submit_pending')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Daily Cash Report Modal */}
        <Dialog open={openDailyReportModal} onClose={() => setOpenDailyReportModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle className="no-print">{t('academy.financial.report.title')}</DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Box id="printable-report" dir={isRtl ? 'rtl' : 'ltr'}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid', borderColor: 'primary.main', pb: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <Logo variant="primary" height={45} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1.2 }}>
                      {agencyLib}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('academy.financial.daily_report_sub')}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap', pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}>{t('academy.financial.report.date', todayStr)}</Typography>
              </Box>

              {/* Document Type Title Banner */}
              <Box sx={{ textAlign: 'center', py: 0.8, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                  {t('academy.financial.report_type_title')}
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ my: 2 }}>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{t('academy.financial.report.cash')}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">{currencySymbol}{todayCash}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{t('academy.financial.report.card')}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">{currencySymbol}{todayCard}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{t('academy.financial.report.transfer')}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="secondary.main">{currencySymbol}{todayTransfer}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">{t('academy.financial.report.total', `${currencySymbol}${todayTotal}`)}</Typography>
              </Paper>

              {/* Edition Footer */}
              {showUserOnEditions && (
                <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{t('academy.financial.issued_by')}</strong> {currentUserName} {user?.profileLib ? `(${user.profileLib})` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {agencyLib} | {new Date().toLocaleDateString(language === 'ar' ? 'ar-DZ' : language === 'en' ? 'en-US' : 'fr-FR')}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions className="no-print">
            <Button onClick={() => setOpenDailyReportModal(false)}>{t('btn.close')}</Button>
            <Button variant="contained" onClick={() => window.print()}>{t('academy.financial.report.print')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </GenericPage>
  );
};

export default FinancialPage;
